/**
 * useTrainingEffectiveness Hook
 * 
 * Track training effectiveness events and outcomes (Catalyst feature).
 * 
 * =====================================================================
 * CANONICAL EVENT TAXONOMY
 * =====================================================================
 * 
 * This hook uses the canonical event types from src/types/training.ts:
 * - impressed: artifact card visible in viewport
 * - opened: external link clicked, new tab opened
 * - engaged: meaningful return detected
 * - completed: completion confirmed via dialog
 * - abandoned: session timeout or explicit dismiss
 * - rated: feedback submitted
 * 
 * For new event logging, prefer useTrainingEvents hook which provides
 * the unified pipeline with sync logging and retry support.
 * 
 * =====================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  TrainingEffectivenessEvent, 
  TrainingJobOutcome,
  CanonicalTrainingEventType,
  LearningSource 
} from '@/types/training';

// Re-export the canonical type for backwards compatibility
export type EffectivenessEventType = CanonicalTrainingEventType;

/**
 * Fetch effectiveness events for a technician
 */
export function useEffectivenessEvents(
  technicianProfileId: string | undefined,
  options?: { limit?: number; artifactId?: string }
) {
  return useQuery({
    queryKey: ['effectiveness-events', technicianProfileId, options],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      let query = supabase
        .from('training_effectiveness_events')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('created_at', { ascending: false });
      
      if (options?.artifactId) {
        query = query.eq('artifact_id', options.artifactId);
      }
      
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      return (data || []) as TrainingEffectivenessEvent[];
    },
    enabled: !!technicianProfileId,
  });
}

interface TrackEffectivenessEventInput {
  technicianProfileId: string;
  artifactId: string;
  eventType: CanonicalTrainingEventType;
  source?: LearningSource;
  ticketId?: string;
  jobCategory?: string;
  rating?: number;
  confidenceDelta?: number;
  timeSpentSeconds?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Track a training effectiveness event
 * 
 * @deprecated For new code, prefer useTrainingEvents hook which provides
 * the unified pipeline with sync logging and retry support.
 */
export function useTrackEffectivenessEvent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: TrackEffectivenessEventInput) => {
      const { data, error } = await supabase
        .from('training_effectiveness_events')
        .insert({
          technician_profile_id: input.technicianProfileId,
          artifact_id: input.artifactId,
          event_type: input.eventType,
          source: input.source || 'learning_center',
          ticket_id: input.ticketId,
          job_category: input.jobCategory,
          rating: input.rating,
          confidence_delta: input.confidenceDelta,
          time_spent_seconds: input.timeSpentSeconds,
          metadata: input.metadata || {},
        } as any)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['effectiveness-events', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Fetch job outcomes for a technician
 */
export function useJobOutcomes(
  technicianProfileId: string | undefined,
  options?: { domainKey?: string; limit?: number }
) {
  return useQuery({
    queryKey: ['job-outcomes', technicianProfileId, options],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      let query = supabase
        .from('training_job_outcomes')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('created_at', { ascending: false });
      
      if (options?.domainKey) {
        query = query.eq('domain_key', options.domainKey);
      }
      
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      return (data || []) as TrainingJobOutcome[];
    },
    enabled: !!technicianProfileId,
  });
}

interface RecordJobOutcomeInput {
  technicianProfileId: string;
  artifactId?: string;
  domainKey: string;
  jobTypeKey?: string;
  ticketId?: string;
  beforeAfterFlag: 'before_training' | 'after_training';
  outcomeMetrics: {
    actual_minutes?: number;
    estimated_minutes?: number;
    variance_minutes?: number;
    comeback_flag?: boolean;
    completion_success?: boolean;
  };
  windowStartAt?: string;
  windowEndAt?: string;
}

/**
 * Record a training job outcome
 */
export function useRecordJobOutcome() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: RecordJobOutcomeInput) => {
      const { data, error } = await supabase
        .from('training_job_outcomes')
        .insert({
          technician_profile_id: input.technicianProfileId,
          artifact_id: input.artifactId,
          domain_key: input.domainKey,
          job_type_key: input.jobTypeKey,
          ticket_id: input.ticketId,
          before_after_flag: input.beforeAfterFlag,
          outcome_metrics: input.outcomeMetrics,
          window_start_at: input.windowStartAt,
          window_end_at: input.windowEndAt,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['job-outcomes', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Get effectiveness summary for a technician
 */
export function useEffectivenessSummary(technicianProfileId: string | undefined) {
  const { data: events } = useEffectivenessEvents(technicianProfileId, { limit: 100 });
  
  return useQuery({
    queryKey: ['effectiveness-summary', technicianProfileId],
    queryFn: () => {
      if (!events || events.length === 0) {
        return {
          totalOpened: 0,
          totalCompleted: 0,
          totalAbandoned: 0,
          completionRate: 0,
          averageRating: null as number | null,
          averageConfidenceDelta: null as number | null,
        };
      }
      
      const opened = events.filter(e => e.event_type === 'opened').length;
      const completed = events.filter(e => e.event_type === 'completed').length;
      const abandoned = events.filter(e => e.event_type === 'abandoned').length;
      
      const ratings = events
        .filter(e => e.rating !== null && e.rating !== undefined)
        .map(e => e.rating as number);
      
      const confidenceDeltas = events
        .filter(e => e.confidence_delta !== null && e.confidence_delta !== undefined)
        .map(e => e.confidence_delta as number);
      
      return {
        totalOpened: opened,
        totalCompleted: completed,
        totalAbandoned: abandoned,
        completionRate: opened > 0 ? (completed / opened) * 100 : 0,
        averageRating: ratings.length > 0 
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length 
          : null,
        averageConfidenceDelta: confidenceDeltas.length > 0
          ? confidenceDeltas.reduce((a, b) => a + b, 0) / confidenceDeltas.length
          : null,
      };
    },
    enabled: !!technicianProfileId && !!events,
  });
}
